// @flow

import { useMemo } from 'react';

import { useDispatch } from 'react-redux';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectHasClaims,
  selectOneProductErrors,
  selectProductOptionsUIFactory,
  selectSmdData,
  selectSmdFileInfos,
  type DecoratedProduct,
  updateProductAndGetCartOrNotify,
  domain,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectShouldShowOtherDomainErrors } from '../../../modules/selectors';
import { useBestCatalogPrice } from '../modules/catalog';
import { IDNErrorRegexp, hasIDNError } from '../selectors';

// eslint-disable-next-line import/no-cycle
import DomainCreateOption from './DomainCreateOption';

import { DomainLabel } from './components/DomainLabel';
import ProductErrorItem from './components/ProductErrorItem';
import ProductItem from './components/ProductItem';
import {
  AlphabetRow,
  ClaimsRow,
  DurationRow,
  FreeMailboxRow,
  SmdFileRow,
} from './components/Rows';

import type { GandiFormSelectOption } from './types';

type Props = {
  product: DecoratedProduct,
};

function DomainCreate({ product }: Props): React.MixedElement {
  const productId = product.id;
  const productIdProps = useMemo(() => ({ productId }), [productId]);

  const translator = useTranslator();
  const { __, formatDate } = translator;
  const dispatch = useDispatch();

  const { durations, priceCrossedOut, total } = useBestCatalogPrice(productId);

  const selectProductOptionsUI = selectProductOptionsUIFactory();
  const productOptions = useAppSelector(state =>
    selectProductOptionsUI(state, productId),
  );
  const productHasClaims = useAppSelector(state =>
    selectHasClaims(state, productIdProps),
  );

  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, productIdProps),
  );
  const productSmdData = useAppSelector(state =>
    selectSmdData(state, productIdProps),
  );
  const smdData = useAppSelector(state =>
    selectSmdFileInfos(state, productIdProps),
  );
  const productHasValidSmdFile = smdData?.isValid || false;

  const authenticated = useAppSelector(selectIsAuthenticatedUser);
  const shouldDisplayProductErrors = useAppSelector(
    selectShouldShowOtherDomainErrors,
  );
  const isCorporate = product.payload.corporate;
  const productLabel = <DomainLabel product={product} />;

  const sunrise = product.payload.tld_phase === 'sunrise';
  const productCanHaveClaims = authenticated && !sunrise;
  const shouldRenderSmd = authenticated && sunrise;

  const shouldRenderClaims =
    !product.isWorking && productCanHaveClaims && productHasClaims;

  const filteredErrors = useMemo(
    () =>
      productErrors
        .filter(
          error =>
            !(
              error.description === 'owner is missing' && error.name === 'owner'
            ),
        )
        .filter(error => !IDNErrorRegexp.test(error.description))
        .filter(error => !(error.name === 'fqdn' && shouldRenderClaims))

        .filter(error => !(error.name === 'smd'))
        // extra-params errors require_x-uk_contact_type
        .filter(error => !/\.x-/.test(error.name)),
    [productErrors, shouldRenderClaims],
  );

  const shouldRenderIDNAvailableLanguages =
    hasIDNError(productErrors) || !!product.payload.lang;

  const shouldShowPhase = domain.shouldShowPhase(product.payload);
  const phaseName = domain.getPhaseNameTranslation({
    tld_phase: product.payload.tld_phase || 'golive',
    translator,
  });
  const releaseDateSentence = __('release %(dateStart)s', {
    dateStart: formatDate(product.payload.phase_date_start, 'LL'),
  });

  if (filteredErrors.length > 0 && shouldDisplayProductErrors)
    return (
      <ProductErrorItem
        key={`${product.id}-error-row`}
        isCorporate={isCorporate}
        label={productLabel}
        detail={shouldShowPhase ? phaseName : undefined}
        info={shouldShowPhase ? releaseDateSentence : undefined}
        productId={product.id}
        errors={filteredErrors}
      />
    );

  const handleDurationChange: (
    selectOption: GandiFormSelectOption<string>,
  ) => void = (selectedOption: GandiFormSelectOption<string>) => {
    const newPayload = {
      ...product.payload,
      duration: parseInt(selectedOption.value, 10),
    };

    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  const handleLangChange: (
    selectedOption: GandiFormSelectOption<string>,
  ) => void = (selectedOption: GandiFormSelectOption<string>) => {
    const newPayload = {
      ...product.payload,
      lang: selectedOption.value,
    };

    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  return (
    <ProductItem
      key={`${product.id}-row`}
      isCorporate={product.payload.corporate}
      productId={product.id}
      label={productLabel}
      detail={shouldShowPhase ? phaseName : undefined}
      info={shouldShowPhase ? releaseDateSentence : undefined}
      footer={
        <DurationRow
          value={product.payload.duration || 1}
          values={durations}
          disabledOption={!!product.isWorking}
          // $FlowFixMe[incompatible-type-arg] - number is number|string
          onChange={handleDurationChange}
          priceCrossedOut={priceCrossedOut}
          productId={product.id}
          total={total}
        />
      }
    >
      {shouldRenderIDNAvailableLanguages && (
        <AlphabetRow
          onChange={handleLangChange}
          disabledOption={!!product.isWorking}
          value={product.payload.lang}
          productId={productId}
        />
      )}

      <FreeMailboxRow product={product} />

      {!!productOptions.length &&
        productOptions.map(option => (
          <DomainCreateOption option={option} key={option.label} />
        ))}
      {shouldRenderSmd && (
        <SmdFileRow
          productId={productId}
          hasValidSmdFile={productHasValidSmdFile}
          productSmdData={productSmdData}
        />
      )}
      {shouldRenderClaims && (
        <ClaimsRow
          productId={productId}
          productClaims={product.payload.claims}
        />
      )}
    </ProductItem>
  );
}

export default DomainCreate;
